import { z } from "zod";

import { PaginatedResponse } from "./pagination.js";
import { MultiPolygon } from "geojson";

export const DEFAULT_PAGE_LIMIT = 100;

export const ZodLink = z.object({
  self: z.string(),
  first: z.string(),
  prev: z.string().nullable(),
  next: z.string().nullable(),
  last: z.string(),
});

export const ZodMeta = z.object({
  count: z.number(),
  totalCount: z.number(),
  totalPages: z.number(),
});

export type FieldStatus = "SUPPORTED" | "CROP_NOT_SUPPORTED" | "INCOMPLETE";

export type FieldData = {
  coverCrop: boolean | undefined;
  irrigation: string | undefined;
  lbsNitrogen: number | undefined;
  previousCrop: string | undefined;
  tillage: string | undefined;
};

const TillingOptionsSchema = z
  .enum(["CONV", "CONS", "No-Till", ""])
  .optional()
  .default("");
export type TillingOptions = z.infer<typeof TillingOptionsSchema>;

const IrrigationEnumSchema = z
  .enum(["Full", "Limited", "None", ""])
  .optional()
  .default("");
export type IrrigationOptions = z.infer<typeof IrrigationEnumSchema>;

const CropOptionsSchema = z
  .enum(["Corn", "Soy", "Other", ""])
  .optional()
  .default("");
export type CropOptions = z.infer<typeof CropOptionsSchema>;

export enum SprayTimingStatus {
  // Incomplete
  INCOMPLETE = "INCOMPLETE",
  // Calculating
  CALCULATING = "CALCULATING",
  // Monitoring
  MONITORING = "MONITORING",
  SUPPORTED = "SUPPORTED",
  // Upcoming Spray - 8-14 days
  UPCOMING_SPRAY = "UPCOMING",
  UPCOMING = "UPCOMING",
  // Spray Soon - 4-7 days
  MEDIUM_PRIORITY = "SPRAY_SOON",
  MEDIUM_PRIORITY_V1 = "LT14",
  SPRAY_SOON = "SPRAY_SOON",
  //Spray Now
  HIGH_PRIORITY = "SPRAY_NOW",
  HIGH_PRIORITY_V1 = "LT7",
  HIGH_PRIORITY_V2 = "0-3 days",
  SPRAY_NOW = "SPRAY_NOW",
  // Overdue Spray
  OVERDUE_SPRAY_SOFT = "OVERDUE_SOFT",
  OVERDUE_SPRAY_HARD = "OVERDUE_HARD",
  OVERDUE_SPRAY_V2 = "Already occured",
  OVERDUE_SOFT = "OVERDUE_SOFT", // 4-7 days after day 0
  OVERDUE_HARD = "OVERDUE_HARD", // 7+ days after day 0
  // Sprayed
  SPRAYED = "SPRAYED",
  // Out of Season
  OUT_OF_SEASON = "OUT_OF_SEASON",
  // Skipped
  SKIPPED = "SKIPPED",
  // Crop not Supported
  CROP_NOT_SUPPORTED = "CROP_NOT_SUPPORTED",
  // Window Passed
  WINDOW_PASSED = "WINDOW_PASSED",
  // Legacy
  NO_DATA_V1 = "NO DATA",
  NO_DATA = "NO_DATA",
  NO_ACTION_NEEDED = "NO_ACTION",
  NO_RECOMMENDATION = "NO_RECOMMENDATION",
  NO_RECOMMENDATION_V2 = "No recommendation",
  LATE_SPRAY = "LATE_SPRAY",
  ERROR = "ERROR",
  IGNORED = "IGNORED",
}

const ZodFieldSummary = z.object({
  operationId: z.string(),
  fieldId: z.string(),
  fieldName: z.string(),
  farmId: z.string(),
  farmName: z.string(),
  fieldAcres: z.number().optional(),
  fieldArea: z.number().optional(),
  cropId: z.string().optional(),
  productName: z.string().optional(),
  plantingDate: z.string().optional(),
  sprayedDate: z.string().datetime().nullable(),
  status: z.nativeEnum(SprayTimingStatus),
  lastStatusChange: z.string().datetime().nullable(),
  incompleteDefinition: z.string(),
  featureErrors: z
    .array(
      z.object({
        feature: z.string(),
        errorType: z.string(),
        errorMessage: z.string(),
        errorDetails: z.record(z.object({})).nullish(),
        status: z.string(),
      }),
    )
    .nullish(),
  modelDiagnostics: z.object({
    nlbRisk: z.string().nullable(),
    glsRisk: z.string().nullable(),
    susceptibility: z.string().nullable(),
    leafWetness: z.string().nullable(),
    rainInMm: z.number().nullable(),
    gdus: z.number().nullable(),
    lastModelRun: z.string().datetime().nullable(),
  }),
  commonCycleId: z.string().uuid(),
  ignored: z.boolean(),
  activityId: z.string().nullable(),
  productId: z.string().nullable(),
  fieldData: z.object({
    previousCrop: z.string().nullable(),
    tillage: z.string().nullable(),
    irrigation: z.string().nullable(),
    lbsNitrogen: z.number().nullable(),
  }),
});

export type FieldsSummary = z.infer<typeof ZodFieldSummary>;

export type ComplementaryFieldSummary = {
  operationName?: string;
  shape?: {
    multiPolygon: MultiPolygon;
    thumbnailSvg?: string;
    isShapeLoading: boolean;
    isShapeError: boolean;
  };
};

// JUST (ALMOST) A COPY OF getV2OperationPathOperationIdFieldsResponse.
// This is just a temporary type until we have the inferred type from the real endpoint
const getV2ContextFieldsResponse = z.object({
  data: ZodFieldSummary.array(),
  metadata: ZodMeta,
  links: ZodLink,
});

export type FieldsSummaryResponse = z.infer<typeof getV2ContextFieldsResponse>;

// Alias for backward compatibility - same structure as FieldsSummaryResponse
export type FieldsByOperationResponse = FieldsSummaryResponse;

export const markAsSprayedOrIgnoredResponseSchema = z.object({
  data: z.array(
    z.object({
      fieldId: z.string(),
      operationId: z.string(),
      retrieved: z.string(),
      modelStartDate: z.string(),
      modelEndDate: z.string(),
      sprayWindow: z.string(),
      activityId: z.string(),
      plannedDate: z.string(),
      completedDate: z.string(),
      rawModelOutput: z.string(),
      skipped: z.boolean(),
      sprayed: z.boolean(),
      sprayedDate: z.string(),
      modelProductId: z.string(),
      fieldData: z.object({
        previousCrop: z.string(),
        tillage: z.string(),
        coverCrop: z.boolean(),
        irrigation: z.string(),
        lbsNitrogen: z.number(),
      }),
      vNextRawModelOutput: z.string(),
    }),
  ),
});

export type MarkAsSprayedResponse = z.infer<
  typeof markAsSprayedOrIgnoredResponseSchema
>;

export type MarkAsIgnoredResponse = z.infer<
  typeof markAsSprayedOrIgnoredResponseSchema
>;

export type CondensedMarkAsSprayedResponse = {
  [operationId: string]: {
    fieldId: string;
    sprayed: boolean;
    sprayedDate: string;
  }[];
};

export type MarkAsSprayedRequestBody = {
  operations: Record<string, string[]>;
  sprayed: boolean;
  sprayedDate?: string;
};

export type OperationsFieldsResponse = z.infer<
  typeof getV2ContextFieldsResponse
>;

export type Recommendation = {
  fieldData: FieldData;
  message: string | undefined;
  previewMessage: string | undefined;
  skipped: boolean | undefined;
  sprayed: boolean;
  sprayedDate: string | undefined;
};

export type ActivitySummary = {
  crop: string | undefined;
  displayName: string | undefined;
  error: string | undefined;
  noOfUnits: number | undefined;
  plantingRate: number | undefined;
  units: string | undefined;
};

export type Activity = {
  activityId: string | undefined;
  activitySummaries: ActivitySummary[];
  completedDate: string | undefined;
  crop: string | undefined;
  cropId: string | undefined;
  cropAssignmentId: string | undefined;
  displayName: string | undefined;
  noOfUnits: number | undefined;
  plannedDate: string | undefined;
  plantingRate: number | undefined;
  units: string | undefined;
};

export type CropVisorBestActivity = {
  activityId: string;
  productId: string;
  date: string | null;
};

export type FieldSummary = {
  activities: Activity[];
  area: number;
  assignedCrops: string[];
  cropInformation: string;
  errors: string[];
  farmId: string | undefined;
  farmName: string | undefined;
  fieldId: string;
  fieldName: string;
  latitude: number;
  longitude: number;
  plantDate: string | undefined;
  minPlantingDate?: string;
  maxPlantingDate?: string;
  productName: string | undefined;
  recommendation: Recommendation;
  status: FieldStatus;
  thumbnailSvg: string;
  bestActivity: CropVisorBestActivity | undefined;
};

export type OperationSummary = {
  businessCycle: string | undefined;
  errors: string[];
  fields: FieldSummary[];
  operationId: string;
  totalFieldArea: number;
  totalFieldCount: number;
};

export type OperationSummariesResponse = PaginatedResponse<OperationSummary>;

export const additionalFieldDataSchema = z.object({
  irrigation: IrrigationEnumSchema,
  tillage: TillingOptionsSchema,
  coverCrop: z.union([z.boolean(), z.number()]).default(false),
  lbsNitrogen: z.string().default("0"),
  previousCrop: CropOptionsSchema,
});

export type AdditionalFieldData = z.infer<typeof additionalFieldDataSchema>;

const additionalFieldDataRequestBodySchema = z.object({
  fieldId: z.string(),
  fieldData: additionalFieldDataSchema,
});

export type AdditionalFieldDataRequestBody = z.infer<
  typeof additionalFieldDataRequestBodySchema
>;

export const additionalFieldDataApiResponseSchema = z.object({
  field_id: z.string(),
  operation_id: z.string(),
  planned_date: z.string(),
  spray_window: z.nativeEnum(SprayTimingStatus),
  model_end_date: z.string(),
  completed_date: z.string(),
  id: z.string(),
  field_data: additionalFieldDataSchema,
  model_start_date: z.string(),
  activity_id: z.string(),
  retrieved: z.string(),
  skipped: z.string(),
});

export type AdditionalFieldDataApiResponse = z.infer<
  typeof additionalFieldDataApiResponseSchema
>;

export const saveFieldDetailsArgsSchema = z.object({
  operationId: z.string(),
  fieldDetailsBody: additionalFieldDataRequestBodySchema,
});

export type SaveFieldDetailsArgs = z.infer<typeof saveFieldDetailsArgsSchema>;

export type SkipCropProtectionRequestItem = {
  fieldId: string;
  operationId: string;
  skipped?: boolean;
  isRegistered: boolean;
};
export type SkipCropProtectionRequestBody = SkipCropProtectionRequestItem[];

export type FieldRegistrationResponse = {
  id: string;
  field_id: string;
  operation_id: string;
  spray_window: string;
  retrieved: string;
  model_start_date: string;
  model_end_date: string;
  activity_id: string;
  planned_date: string;
  completed_date: string;
  skipped: string;
  field_data: FieldData;
};

export type Acl = {
  d: string[];
  a: string[];
};

export type FieldInfo = {
  operationId: string;
  operationName: string;
  fieldId: string;
  fieldName: string;
  farmId: string;
  farmName: string;
  fieldArea: number;
  skipped: boolean;
  sprayed: boolean;
  isSupported: boolean;
  status: SprayTimingStatus;
  bestActivity?: {
    activityId: string;
    date: string | null;
    displayName: string;
    productId: string;
  };
  incompleteDefinition?: string;
  isEnrolled?: boolean;
  productName?: string;
  shape?: string;
  sprayedDate?: string;
  state?: SprayTimingStatus;
};

export type FieldStatusModel = {
  fieldId: string;
  fieldName: string;
  status: string;
};

export type FieldsStatusReportRequestModel = {
  operationId: string;
  operationName: string;
  fieldsStatusList: FieldStatusModel[];
};

export type GenerateReportRequest = {
  cropYear: string;
  locale: string;
  businessCycleId: string;
  reportType: string;
  payload: FieldsStatusReportRequestModel[];
  options?: Record<string, unknown>;
};

export type ReportScheduleDetails = {
  operationId: string;
  commonCycleId: string;
  fieldList: string[];
  config: Record<string, unknown>;
  email: string;
}

export type CreateReportScheduleRequest = {
  reports: ReportScheduleDetails[];
}

export type CreateReportScheduleResponseModel = {
  failedOperations: string[];
}

export type FieldsStatusReportResponseModel = {
  successfulOperations: string[];
  failedOperations: string[];
};

export type FieldSkipData = {
  operationId: string;
  commonCycleId?: string;
  fields: Array<{
    fieldId: string;
    skipped: boolean;
  }>;
};

export type FieldIgnoreData = {
  operationId: string;
  commonCycleId: string;
  fields: Array<{
    fieldId: string;
    ignored: boolean;
  }>;
};

export const fieldAttributeValueV3schema = z
  .object({
    value: z.string().optional(),
    smartDefault: z.boolean().optional(),
    timestamp: z.string().optional(),
  })
  .optional();

export const RegisterFieldAttributesV3PayloadItemSchema = z.object({
  commonCycleId: z.string(),
  operationId: z.string(),
  fieldId: z.string(),
  hasUserUpdatedPreviousCrop: z.boolean().default(false),
  fieldData: z.object({
    previousCrop: z.string().optional(),
    tillage: fieldAttributeValueV3schema,
    irrigation: fieldAttributeValueV3schema,
    lbsNitrogen: fieldAttributeValueV3schema,
  }),
});

const registerFieldAttributesV3Payload = z.array(
  RegisterFieldAttributesV3PayloadItemSchema,
);

export type RegisterFieldAttributesV3Payload = z.infer<
  typeof registerFieldAttributesV3Payload
>;

export const RegisterFieldAttributesV3ResponseSchema = z.array(
  z.object({
    operationId: z.string(),
    fieldId: z.string(),
    tillage: z.string(),
    irrigation: z.string(),
    nitrogenRange: z.string(),
  }),
);

export type RegisterFieldAttributesV3Response = z.infer<
  typeof RegisterFieldAttributesV3ResponseSchema
>;

const TillingOptionsV3Schema = z
  .enum(["TILLAGE_UNKNOWN", "NO_TILL", "REDUCED_TILL", "CONVENTIONAL_TILL"])
  .optional()
  .default("TILLAGE_UNKNOWN");

export type TillingOptionsV3 = z.infer<typeof TillingOptionsSchema>;

const IrrigationOptionsV3Schema = z
  .enum(["IRRIGATION_UNKNOWN", "IRRIGATED", "DRYLAND", "LIMITED"])
  .optional()
  .default("IRRIGATION_UNKNOWN");

export type IrrigationOptionsV3 = z.infer<typeof IrrigationEnumSchema>;

const validNitrogenPayload = z
  .enum(["130", "150", "170", "190", "210", "230"])
  .optional();

export type ValidNitrogenPayload = z.infer<typeof validNitrogenPayload>;

export const smartDefaultDataSchema = z
  .object({
    crop: CropOptionsSchema,
    tillage: TillingOptionsV3Schema,
    irrigation: IrrigationOptionsV3Schema,
    nitrogen: validNitrogenPayload,
  })
  .optional();

export type SmartDefaultData = z.infer<typeof smartDefaultDataSchema>;

export const smartDefaultsSchema = z.object({
  operationId: z.string().uuid(),
  commonCycleId: z.string().uuid(),
  defaults: z.object({
    corn: smartDefaultDataSchema,
    soy: smartDefaultDataSchema,
  }),
});

export const smartDefaultsArray = z.array(smartDefaultsSchema);

export type SmartDefaultsPayload = z.infer<typeof smartDefaultsArray>;

export const smartDefaultArgs = z.object({
  contextId: z.string().optional(),
  smartDefaults: smartDefaultsArray,
});

export type SmartDefaultArgs = z.infer<typeof smartDefaultArgs>;

export const smartDefaultsResponseSchema = z.object({
  data: z.object({
    processedOperations: z.array(z.string()),
  }),
});

export type SmartDefaultsResponse = z.infer<typeof smartDefaultsResponseSchema>;

export const fieldAttributesV3Schema = z
  .object({
    commonCycleId: z.string().optional(),
    operationId: z.string().optional(),
    fieldId: z.string().optional(),
    previousCrop: fieldAttributeValueV3schema,
    tillage: fieldAttributeValueV3schema,
    irrigation: fieldAttributeValueV3schema,
    nitrogen: fieldAttributeValueV3schema,
  })
  .optional();

export type FieldAttributesV3Response = z.infer<typeof fieldAttributesV3Schema>;
